import GoogleSignInButton from "./GoogleSignInButton";

function isLocalDevHost() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
}

const BRAND_ICONS = {
  google: (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.6h5.1c-.2 1.2-1.6 3.5-5.1 3.5-3.1 0-5.6-2.5-5.6-5.6S8.9 6.1 12 6.1c1.8 0 3 .8 3.7 1.4l2.5-2.4C16.9 3.7 14.6 2.7 12 2.7 6.9 2.7 2.7 6.9 2.7 12S6.9 21.3 12 21.3c6.9 0 8.6-4.8 8.6-7.2 0-.5 0-.9-.1-1.3H12z" />
      <path fill="#34A853" d="M3.9 7.5l3 2.2c.7-1.7 2.4-2.9 4.4-2.9 1.2 0 2.3.4 3.1 1.1l2.3-2.3C14.8 4.3 13.5 3.8 12 3.8 8.5 3.8 5.6 6.1 4.6 9.3l-.7-.8z" />
      <path fill="#4A90E2" d="M12 21.3c2.7 0 5-.9 6.6-2.4l-3.1-2.5c-.9.6-2 .9-3.5.9-2.7 0-4.9-1.8-5.7-4.3l-3.1 2.4c1.5 3 4.7 4.9 8.8 4.9z" />
      <path fill="#FBBC05" d="M20.6 13.8c.1-.5.2-1 .2-1.5 0-.5-.1-1-.2-1.5H12v3.1h4.8c-.2 1.1-.9 2-1.9 2.6l3.1 2.4c1.8-1.7 2.6-4.2 2.6-7.1z" />
    </svg>
  ),
  facebook: (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#1877F2" d="M24 12.07C24 5.41 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.02 10.13 11.91v-8.41H7.08v-3.5h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.23 2.68.23v2.95h-1.51c-1.49 0-1.95.93-1.95 1.88v2.26h3.32l-.53 3.5h-2.79v8.41C19.61 23.09 24 18.09 24 12.07z" />
    </svg>
  ),
  twitter: (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#111827" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  apple: (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#111827" d="M16.365 1.43c0 1.14-.42 2.09-1.24 2.86-.9.84-1.95 1.25-3.08 1.17-.14-1.1.42-2.24 1.22-3.01.88-.85 2.02-1.33 3.1-1.33.01.11.01.21 0 .31zm1.56 15.24c-.74.86-1.62 1.54-2.64 2.04-1 .49-2.08.74-3.24.74-1.55 0-2.98-.45-4.29-1.35-1.31-.9-2.34-2.1-3.09-3.6-.76-1.5-1.14-3.14-1.14-4.92 0-1.64.36-3.12 1.08-4.44.72-1.32 1.7-2.34 2.94-3.06 1.24-.72 2.58-1.08 4.02-1.08.98 0 1.9.18 2.76.54.86.36 1.58.8 2.16 1.32l-1.74 2.1c-.92-.72-1.98-1.08-3.18-1.08-1.04 0-1.98.28-2.82.84-.84.56-1.48 1.3-1.92 2.22-.44.92-.66 1.92-.66 3 0 1.16.28 2.24.84 3.24.56 1 1.34 1.8 2.34 2.4 1 .6 2.12.9 3.36.9.92 0 1.76-.18 2.52-.54.76-.36 1.42-.84 1.98-1.44l1.62 2.04z" />
    </svg>
  ),
  microsoft: (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M13 1h10v10H13z" />
      <path fill="#00A4EF" d="M1 13h10v10H1z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  ),
};

const GRID_ORDER = ["facebook", "google", "twitter", "apple"];

export default function SocialLoginPanel({
  providers = [],
  googleClientId = null,
  loading = false,
  onGoogleCredential,
  onProviderNotice,
}) {
  const byId = Object.fromEntries((providers || []).map((p) => [p.id, p]));
  const microsoft = byId.microsoft;
  const gridItems = GRID_ORDER.map((id) => byId[id]).filter(Boolean);
  // Google's hosted button logs a 403 and GSI_LOGGER when this origin is not
  // allow-listed. Local dev uses the same first-party button as the other providers.
  const googleWidget = Boolean(googleClientId) && !isLocalDevHost();

  const startProvider = (provider) => {
    if (loading || !provider) return;
    if (provider.id === "google" && googleWidget) return;
    if (provider.enabled && provider.login_url) {
      window.location.href = provider.login_url;
      return;
    }
    onProviderNotice?.(
      `${provider.label} sign-in is not configured on the server yet. Add OAuth keys to backend/.env and restart the API.`
    );
  };

  return (
    <div className="bg-social-login">
      {microsoft && (
        <button
          type="button"
          className={`bg-social-primary${microsoft.enabled ? " is-live" : ""}`}
          onClick={() => startProvider(microsoft)}
          disabled={loading}
        >
          {BRAND_ICONS.microsoft}
          <span>Continue with Microsoft</span>
        </button>
      )}

      <div className="bg-social-kicker subtle">Or with</div>
      <div className="bg-social-grid">
        {gridItems.map((provider) => {
          if (provider.id === "google" && googleWidget) {
            return (
              <div key="google" className="bg-social-tile bg-social-tile-google is-live">
                <GoogleSignInButton
                  clientId={googleClientId}
                  onSuccess={onGoogleCredential}
                  disabled={loading}
                />
              </div>
            );
          }

          return (
            <button
              key={provider.id}
              type="button"
              className={`bg-social-tile brand-${provider.brand || provider.id}${provider.enabled ? " is-live" : ""}`}
              onClick={() => startProvider(provider)}
              disabled={loading}
            >
              <span className="bg-social-tile-icon">{BRAND_ICONS[provider.brand || provider.id]}</span>
              <span className="bg-social-tile-label">{provider.label}</span>
            </button>
          );
        })}
      </div>

      {microsoft && (
        <button
          type="button"
          className="bg-social-sso-link"
          onClick={() => startProvider(microsoft)}
          disabled={loading}
        >
          Log in with <strong>company SSO</strong>
        </button>
      )}
    </div>
  );
}
